import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppModal from 'hooks/useAppModal';
import SeasonalRuleAddEditModal from './SeasonalRuleAddEditModal';
import { seasonalRuleModalId } from './SeasonalRuleAddEditModal.constants';
import { toPayload } from './SeasonalRuleAddEditModal.schema';
import { SeasonalRuleFormValues } from './SeasonalRuleAddEditModal.types';
import useSeasonalRuleMutation from './useSeasonalRuleMutation';

const useSeasonalRuleModal = () => {
  const { openModal, closeModal } = useAppModal();
  const { saveSeasonalRule } = useSeasonalRuleMutation();
  const navigate = useNavigate();

  const onHide = useCallback(() => {
    closeModal(seasonalRuleModalId);
    navigate('../', { replace: true });
  }, [navigate, closeModal]);

  const buildHandleSubmit = useCallback(
    (seasonalRuleUid?: string) => (values: SeasonalRuleFormValues) =>
      saveSeasonalRule({
        uid: seasonalRuleUid,
        payload: toPayload(values),
        successCallback: onHide,
      }),
    [saveSeasonalRule, onHide],
  );

  const openAddSeasonalRuleModal = useCallback(
    (args?: { currentProperty?: { uid: string; name: string } }) => {
      openModal({
        id: seasonalRuleModalId,
        customContent: (
          <SeasonalRuleAddEditModal
            onSubmit={buildHandleSubmit()}
            currentProperty={args?.currentProperty}
            closeModal={onHide}
          />
        ),
        size: 'lg',
        backdrop: 'static',
        closeOnHide: false,
        onHide,
      });
    },
    [openModal, onHide, buildHandleSubmit],
  );

  const openEditSeasonalRuleModal = useCallback(
    ({
      seasonalRuleUid,
      currentProperty,
    }: {
      seasonalRuleUid: string;
      currentProperty?: { uid: string; name: string };
    }) => {
      openModal({
        id: seasonalRuleModalId,
        customContent: (
          <SeasonalRuleAddEditModal
            seasonalRuleUid={seasonalRuleUid}
            onSubmit={buildHandleSubmit(seasonalRuleUid)}
            currentProperty={currentProperty}
            closeModal={onHide}
          />
        ),
        size: 'lg',
        backdrop: 'static',
        closeOnHide: false,
        onHide,
      });
    },
    [openModal, onHide, buildHandleSubmit],
  );

  return { openAddSeasonalRuleModal, openEditSeasonalRuleModal };
};

export default useSeasonalRuleModal;
